import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { AudiobookTimelineClip } from "../lib/types.js";
import { Icon } from "../components/Icon.js";

interface Props {
  projectId: string;
  scope: "book" | "chapter";
  chapterId?: string;
  voice: string;
  title: string;
  onClose: () => void;
}

/**
 * Read-along audiobook player ("lyrics"): plays the rendered audio and highlights
 * the clip being spoken, scrolling to follow. Click any line to jump there.
 */
export function AudiobookPlayer({ projectId, scope, chapterId, voice, title, onClose }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const [timeline, setTimeline] = useState<AudiobookTimelineClip[] | null>(null);
  const [active, setActive] = useState(-1);

  const opts = useMemo(() => ({ scope, chapterId, voice }), [scope, chapterId, voice]);
  const audioUrl = api.audiobookAudioUrl(projectId, opts);

  useEffect(() => {
    let on = true;
    api
      .audiobookTiming(projectId, opts)
      .then((t) => on && setTimeline(t.timeline))
      .catch(() => on && setTimeline([]));
    return () => {
      on = false;
    };
  }, [projectId, opts]);

  // Find the clip playing at the current time and highlight it.
  const onTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    const tl = timeline;
    if (!audio || !tl) return;
    const t = audio.currentTime * 1000;
    // Linear scan is fine (hundreds of clips); start near the last active index.
    let i = active >= 0 && tl[active] && t >= tl[active]!.startMs ? active : 0;
    while (i < tl.length && t >= tl[i]!.startMs + tl[i]!.durationMs) i++;
    while (i > 0 && t < tl[i]!.startMs) i--;
    if (i !== active && i < tl.length) setActive(i);
  }, [timeline, active]);

  // Keep the highlighted line in view.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [active]);

  const seekTo = (clip: AudiobookTimelineClip) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = clip.startMs / 1000;
    void audio.play();
  };

  return (
    <div className="ab-player">
      <div className="ab-player-head">
        <Icon name="volume" size={15} style={{ color: "var(--accent)" }} />
        <strong className="truncate">{title}</strong>
        <button className="btn btn-icon btn-ghost btn-sm" title="Close player" style={{ marginLeft: "auto" }} onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </div>
      <audio ref={audioRef} src={audioUrl} controls autoPlay onTimeUpdate={onTimeUpdate} className="ab-audio" />
      <div className="ab-transcript">
        {timeline === null ? (
          <p className="muted">Loading transcript…</p>
        ) : timeline.length === 0 ? (
          <p className="muted">No transcript available for this recording. Re-render it to enable read-along.</p>
        ) : (
          timeline.map((clip, i) => {
            const prev = timeline[i - 1];
            const sceneChanged = !prev || prev.sceneTitle !== clip.sceneTitle || prev.chapterTitle !== clip.chapterTitle;
            return (
              <div key={i}>
                {sceneChanged && <div className="ab-scene-head">{clip.chapterTitle} · {clip.sceneTitle}</div>}
                <button
                  ref={i === active ? activeRef : undefined}
                  className={`ab-line ${i === active ? "is-active" : ""}`}
                  onClick={() => seekTo(clip)}
                >
                  {clip.text}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
